/**
 * DataLayer Proxy for Zephr Integration
 * Since Zephr adds all subscription/registration events to the dataLayer, we only need to add SID tracking to them
 * Targeted proxy that intercepts all dataLayer.push() calls to enhance Zephr events with:
 * - Missing SID (promotion_id) parameters from URL or sessionStorage
 * - Missing ecommerce item_brand and currency parameters
 */
(() => {
	// Initialize dataLayer if it doesn't exist
	window.dataLayer ??= [];

	// Get SID from URL or sessionStorage
	function getSidParam() {
		const urlSid = new URL(window.location.href).searchParams.get('sid');
		const sessionSid = sessionStorage.getItem('adw-sid-param');
		return urlSid || sessionSid || undefined;
	}

	//handler for the dataLayer.push method proxy
	const pushHandler = {
		/**
		 * @param {Function} target - The original push method
		 * @param {Object} thisArg - The this value for the original push method
		 * @param {Array} argumentsList - The arguments passed to the original push method
		 */
		apply(target, thisArg, argumentsList) {
			if (!argumentsList.length) {
				return Reflect.apply(target, thisArg, argumentsList);
			}

			const enhancedEvents = [];
			const sidParam = getSidParam();

			for (const event of argumentsList) {
				// Pass through non-objects and non-events unchanged
				if (!event || typeof event !== 'object' || !event.event || !event.ecommerce) {
					enhancedEvents.push(event);
					continue;
				}

				enhancedEvents.push({ ecommerce: null });

				if (sidParam) {
					event.ecommerce.promotion_id ??= sidParam;
				}

				if ('items' in event.ecommerce && Array.isArray(event.ecommerce.items)) {
					event.ecommerce.items = event.ecommerce.items.map(item => {
						item.item_brand = 'subscription';
						item.currency = 'USD';
						if (event.event === 'purchase') {
							item.coupon = event.ecommerce.coupon || undefined;
						}
						return item;
					});
				}

				enhancedEvents.push(event);
			}

			return Reflect.apply(target, thisArg, enhancedEvents);
		}
	};

	// Create proxy for the dataLayer.push method
	window.dataLayer.push = new Proxy(window.dataLayer.push.bind(window.dataLayer), pushHandler);
})();

// fetch with timeout
async function getBackendData(url) {

	// https://dmitripavlutin.com/timeout-fetch-request
	async function fetchWithTimeout(resource, options = {}) {
		const {timeout = 25000} = options;

		const controller = new AbortController();
		const id = setTimeout(() => controller.abort(), timeout);
		const response = await fetch(resource, {
			...options,
			signal: controller.signal
		});
		clearTimeout(id);
		return response;
	}

	try {
		const data = await fetchWithTimeout(url);

		const response = await data.json();

		return response || {};
	} catch (error) {

		console.error('Error getting data from the backend', error);

		return {};
	}
}

const awGa4DL = loadedData => {

	const URLObject = new URL(window.location.href);
	const URLPath = URLObject.pathname.split('/').filter(item => !!item);

	const sidParam = URLObject.searchParams.get('sid') || sessionStorage.getItem('adw-sid-param') || undefined;

	const backendData = window.awGa4;

	const isUserLoggedIn = ('function' === typeof window.awGetUserData) && (0 !== Object.keys(window.awGetUserData()).length);

	// If an array, then it's empty because it would be an object otherwise
	const isEmptyResult = obj => Array.isArray(obj);

	const getContentGrouping = () => {

		let group, type;

		const setStringCapitalization = str => {

			if ('string' !== typeof str) {
				return str;
			}

			const subject = str.toLowerCase();

			return subject.charAt(0).toUpperCase() + subject.slice(1);
		};

		switch (URLPath[0]) {

			// Home Page
			case undefined:
				group = type = 'Home';
				break;

			// Login Page
			case 'login':
				group = 'Account';
				type = 'Login';
				break;

			// Billing Info Page
			case 'billing-info':
				group = 'Account';
				type = 'Billing info';
				break;

			// Newsletter Preferences Page
			case 'newsletter-preferences':
				group = 'Account';
				type = 'Newsletter preferences';
				break;

			// Subscription Info Page
			case 'subscription-info':
				group = 'Account';
				type = 'Subscription info';
				break;

			// User Settings
			case 'user-profile-settings':
				group = type = 'Account';
				break;

			// About Page
			case 'about':
				group = type = 'About';
				break;

			// Corporate Subscriptions
			case 'corporate-subscriptions':
				group = 'Corporate subscriptions';
				type = 'Sales';
				break;

			// Media Kit
			case 'mediakit':
				group = type = 'Media kit';
				break;

			// Newsletter
			case 'newsletter-signup':
				group = 'Newsletter';
				type = 'Newsletter sign up';
				break;

			// Privacy policy
			case 'privacy-policy':
				group = type = 'Privacy policy';
				break;

			// Events Page
			case 'events':
				group = 'Events';
				type = 'Events home';
				break;

			// Webinars Page
			case 'webinars':
				group = 'Webinars';
				type = 'Webinars home';
				break;

			// Resource Library
			case 'resource-library':
				group = type = 'Resource library';

				// If the child post and not the archive
				if (URLPath.length > 1) {
					type = 'Resource';
				}
				break;

			// Podcasts
			case 'podcasts':
				group = 'Podcasts';
				type = 'Podcasts home';

				// If the child post and not the archive
				if (URLPath.length > 1) {
					type = 'Podcast';
				}
				break;

			// Sponsored
			case 'sponsored':
				group = 'Sponsored';
				type = 'Sponsored home';

				// If the child post and not the archive
				if (URLPath.length > 1) {
					type = 'Sponsored content';
				}
				break;

			// Partner Articles
			case 'partner-articles':
				group = 'Sponsored';
				type = 'Partner articles home';

				// If the child post and not the archive
				if (URLPath.length > 1) {
					type = 'Partner article';
				}
				break;

			// Learning
			case 'learning':
				group = 'Learning';
				type = 'Learning home';

				// If the child post and not the archive
				if (URLPath.length > 1) {
					type = 'Learning';
				}

				//if Micro-learning
				if ('microlearning' === URLPath[1]) {

					// Micro-Learning archive or its child post
					type = (2 === URLPath.length) ?
						'Micro-learning home' :
						'Micro-learning';
				}
				break;

			// Inside the brand pages
			// for custom pages inside this vertical
			case 'inside-the-brand':
				// Not vertical home and not an article
				if (URLPath.length > 1 && !(backendData?.post_data?.post_type)) {
					group = document.title;
					type = 'Inside the brand';
				}
				break;

			// Contributor
			case 'contributor':
				group = type = 'Contributor';
				break;

			// Press releases
			case 'press':
				group = 'Press releases';
				type = 'General';
				break;

			// Adweek Wire
			case 'adweek-wire':
				group = 'Adweek wire';
				type = 'Sales';
				break;

			// Magazine issues
			case 'adweek-magazine-issues':
				group = 'Magazine issues';
				type = 'Magazine';
				break;

			// Magazine issue
			case 'issue':
				group = document.title;
				type = 'Issue';
				break;

			// Partner Experts
			case 'partner-experts':
				group = 'Partner experts';
				type = 'Content studio';
				break;

			// Expert Info
			case 'expert-info':
				group = 'Expert info';
				type = 'Content studio';
				break;

			// Expert Info
			case 'partner-info':
				group = 'Partner experts';
				type = 'Content studio';
				break;

			// FAQ
			case 'faq':
				group = 'FAQ';
				type = 'General';
				break;

			// Event Calendar
			case 'event-calendar':
			case 'editorial-calendar':
				group = 'Event Calendar';
				type = 'General';
				break;

			// Lead Gen Info
			case 'lead-gen':
				group = 'Lead Gen Info';
				type = 'Sales';
				break;

			// Thought Leadership
			case 'thought-leadership':
				group = 'Thought Leadership';
				type = 'Sales';
				break;

			// Brand Awareness
			case 'brand-awareness':
				group = 'Brand Awareness';
				type = 'Sales';
				break;

			// Awards
			case 'awards':
				group = type = 'Awards';
				break;

			// Glossary
			case 'glossary':
				group = 'Glossary';
				type = 'General';
				break;

			// Business of Marketing
			case 'the-business-of-marketing':
				group = 'Business of Marketing';
				type = 'Inside the Brand';
				break;

			// Video
			case 'adweek-original-video':
				group = type = 'Video';
				break;

			// Academic Council
			case 'the-adweek-academic-council':
				group = 'Academic Council';
				type = 'Council';
				break;

			// Mediaweek Council
			case 'the-mediaweek-council':
				group = 'Mediaweek Council';
				type = 'Council';
				break;

			// Sustainability Couuncil
			case 'sustainability-council':
				group = 'Sustainability Couuncil';
				type = 'Council';
				break;
			// Academic Council

			case 'academic-council':
				group = 'Academic Council';
				type = 'Council';
				break;

			// Social Media Council
			case 'social-media-council':
				group = 'Social Media Council';
				type = 'Council';
				break;
		}

		// Vertical Archive Page
		if (!isEmptyResult(backendData.vertical_archive_data)) {
			group = backendData.vertical_archive_data.name;
			type = 'Vertical home';
		}

		// Category Archive Page
		if (!isEmptyResult(backendData.category_archive_data)) {
			group = backendData.category_archive_data.name;
			type = 'Category';
		}

		// Article page
		if (
			!isEmptyResult(backendData.post_data) &&
			'post' === backendData.post_data.post_type
		) {
			group = backendData.post_data.vertical_name;
			type = 'Article';
		}

		// Select package page
		if (document.querySelector('#aw-subscriptions')) {
			group = 'Subscription';
			type = 'Select package';
		}

		// Registration
		if (document.querySelector('.blaize-form-register, .blaize-form-capture')) {
			group = 'Account';
			type = 'Registration';
		}

		// Subscription
		if (document.querySelector('.payment-form-wrapper')) {
			group = type = 'Subscription';
		}

		return {
			group: setStringCapitalization(group),
			type: setStringCapitalization(type),
		};
	};

	const getWallData = () => {;
		const classSet = new Set(document.body?.className?.split(/\s+/) || []);

		const URLObject = new URL(window.location.href);
		const URLPath = URLObject.pathname.split('/').filter(Boolean);

		const isUserLoggedIn = !!(window.Zephr || {})?.accessDetails?.isAuthenticated;

		const isResourceLibraryArticle = classSet.has('single-adw_resource');
		const isSingleArticle = classSet.has('single-post');
		const isMicrolearningLesson = URLPath[1] === 'microlearning' && URLPath.length > 2;
		const isLearningCta = !!document.querySelectorAll('.aw-course-subscribe-link').length;

		const zephrFormData = window.dataLayer?.find(item => item.event === 'zephr_form_render') || {};
		const formType = zephrFormData.formType?.toLowerCase() || '';
		const wallLabel = window.adwArticleWallLabel;

		const result = {
			wallType: undefined,
			regWall: undefined,
			payWall: undefined,
			leadWall: undefined,
			noWall: undefined
		};

        const elTreatmentAPaywall = document.getElementById('treatment-a-paywall');
        const elTreatmentBPaywall = document.getElementById('treatment-b-paywall');

		if (isSingleArticle) {
			if (formType === 'payment' || elTreatmentAPaywall || elTreatmentBPaywall ) {
				result.wallType = 'Pay Wall';
				result.payWallLabel = wallLabel;
				result.payWall = 1;
			} else if (formType === 'registration') {
				result.wallType = 'Pay Wall';
				result.payWallLabel = wallLabel;
				result.payWall = 1;
			} else if (formType === 'registration') {
				result.wallType = 'Reg Wall';
				result.regWallLabel = wallLabel;
				result.regWall = 1;
			} else {
				result.wallType = 'No Wall';
				result.noWall = 1;
			}
		} else if (isResourceLibraryArticle) {
			result.wallType = 'Lead Wall';
			result.leadWall = 1;
		} else if (!isUserLoggedIn && (isMicrolearningLesson || isLearningCta)) {
			result = {
				wallType: 'Reg Wall',
				regWall: 1
			};
		}

		window.adwGa4WallData ??= result;
		return result;
	};

	const getPostData = () => {

		const result = {};

		const postData = backendData.post_data;

		// Content Grouping
		const contentGrouping = getContentGrouping();

		result.content_group = contentGrouping.group || undefined;
		result.content_type = contentGrouping.type || undefined;

		// Wall types
		const wallData = getWallData();
		result.content_wall = wallData.wallType || undefined;

		result.reg_wall_content_view = wallData.regWall || undefined;
		result.pay_wall_content_view = wallData.payWall || undefined;
		result.lead_wall_content_view = wallData.leadWall || undefined;
		result.no_wall_content_view = wallData.noWall || undefined;

		if (isEmptyResult(postData)) {
			return result;
		}

		let isSponsoredContentView;

		switch (postData.post_type) {
			case 'adw_resource':
			case 'partner_articles':
			case 'sponsored_post':
				isSponsoredContentView = 1;
		}

		result.isSponsoredContentView = isSponsoredContentView;

		if (
			'adw_resource' !== postData.post_type &&
			'adweek_wire' !== postData.post_type &&
			'partner_articles' !== postData.post_type &&
			'sponsored_post' !== postData.post_type &&
			'post' !== postData.post_type
		) {
			return result;
		}

		if (postData.categories.length) {
			result.categories = postData.categories.join(',');
		}

		result.featuredCategory = postData.featured_category || undefined;
		result.contributors = postData.contributors || undefined;
		result.id = postData.id || undefined;
		result.tags = postData.vertical_slug || undefined;
		result.publishDate = postData.publish_date || undefined;
		result.publishDateMonthYear = postData.publish_date_month_year || undefined;

		return result;
	};

	const getUserData = () => {

		const data = {};

		const awUserData = 'function' === typeof window.awGetUserData ?
			window.awGetUserData() :
			{};

		data.country = awUserData.country_code || undefined;

		data.user_org_id = loadedData.backendUserData?.corporate_accounts || undefined;

		data.status = () => {
			switch (awUserData.sub_type) {
				case 'S':
					return 'subscriber';
				case 'R':
					return 'registered';
			}

			return 'anonymous';
		};

		data.membershipType = () => {

			if ('subscriber' !== data.status()) {
				return undefined;
			}

			return data.user_org_id ? 'org' : 'individual';
		};

		data.user_id = loadedData.backendUserData?.user_id || undefined;

		data.job_industry = awUserData.business_type || undefined;
		data.job_role = awUserData.job_function || undefined;
		data.job_level = awUserData.btitle || undefined;

		data.user_lytics_user_id = loadedData?.lyticsUserData?.userId || undefined;
		data.user_lytics_audience_id = loadedData?.lyticsUserData?.audienceID || undefined;

		data.user_zephr_test_groups = loadedData?.zephrExperiments || undefined;

		return data;
	};

	//PageView
	(() => {
		document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', pageView) : pageView();

		function pageView() {
			const postData = getPostData();
			const userData = getUserData();

			const pageView = {
				'event': 'page_view',
				'event_data': {
					'content_group': postData.content_group,
					'content_type': postData.content_type,
					'content_featured_category': postData.featuredCategory,
					'content_categories': postData.categories,
					'content_tags': postData.tags,
					'content_id': postData.id,
					'content_author': postData.contributors,
					'content_publish_date': postData.publishDate,
					'content_publish_month_year': postData.publishDateMonthYear,
					'content_wall': postData.content_wall,
					'page_location': window.location.href,
					'reg_wall_content_view': postData.reg_wall_content_view,
					'pay_wall_content_view': postData.pay_wall_content_view,
					'lead_wall_content_view': postData.lead_wall_content_view,
					'no_wall_content_view': postData.no_wall_content_view,
					'sponsored_content_view': postData.isSponsoredContentView,
				},
				'user_data': {
					'user_id': userData.user_id,
					'user_org_id': userData.user_org_id,
					'user_status': userData.status(),
					'user_membership_type': userData.membershipType(),
					'user_industry': userData.job_industry,
					'user_job_role': userData.job_role,
					'user_job_level': userData.job_level,
					'user_country': userData.country,
					'user_lytics_user_id': userData.user_lytics_user_id,
					'user_lytics_audience_id': userData.user_lytics_audience_id,
					'user_zephr_test_groups': userData.user_zephr_test_groups,
				}
			};

			window.dataLayer.push(pageView);
		}
	})();

	// Podcasts
	(() => {
		const podcastsHomeSelector = '#adw-podcast-shows';
		const podcastPageSelector = '#podcast-show-app';

		if (!document.querySelector(`${podcastsHomeSelector}, ${podcastPageSelector}`)) {
			return;
		}

		const isPodcastHome = !!document.querySelector(podcastsHomeSelector);

		document.addEventListener('play', event => {
			const player = event.target.querySelector('.pn-audio-player');
			const episodeNumber = player.dataset.episodeNumber || '';
			const episodeTitle = player.dataset.episodeTitle || '';
			let showTitle;

			if (isPodcastHome) {
				const wrapper = player.closest('.player-row');
				const titleWrapper = wrapper.querySelector('.show-title');
				showTitle = titleWrapper.innerText;
			} else {
				showTitle = document.title;
			}

			window.dataLayer.push({
				event: 'podcast_play',
				event_data: {
					podcast_name: showTitle,
					podcast_episode: `EP ${episodeNumber} ${episodeTitle}`,
					podcast_play: 1
				}
			});
		});

		document.addEventListener('click', event => {
			let button;

			if (isPodcastHome) {
				button = event.target;
			} else {
				button = event.target.closest('.js-subscribe-to-podcast');
			}

			//in case of Podcast home page
			if (!button || !button.classList.contains('js-subscribe-to-podcast')) {
				return;
			}

			window.dataLayer.push({
				event: 'podcast_click_subscribe',
				event_data: {
					podcast_name: button.dataset.showTitle
				}
			});
		});
	})();

	// Lead forms
	(() => {
		const leadPages = [
			'event-calendar',
			'editorial-calendar',
			'about',
			'lead-gen',
			'thought-leadership',
			'brand-awareness',
			'mediakit',
			'academic-council',
			'resource-library',
			'adweek-student-subscription-interest-form',
		];

		const pushData = formId => {
			window.dataLayer.push({
				event: 'generate_lead',
				event_data: {
					form_id: formId,
					generate_lead: 1
				}
			});
		};

		if (!leadPages.includes(URLPath[0])) {
			return;
		}

		document.addEventListener('wpcf7submit', event => {
			pushData(event.detail.contactFormId);
		}, false);

		const convertrForm = document.querySelector('#convertr-form');

		if (!convertrForm) {
			return;
		}

		convertrForm.addEventListener('aw_convertr_form_submitted', event => {

			pushData(event.target.dataset.campaign_id);
		});
	})();

	// Newsletter subscription
	(() => {

		const pushData = () => {
			window.dataLayer.push({
				event: 'content_subscribe'
			});
		};

		document.addEventListener('aw-newsletter-subscribe-email-submit', () => {
			pushData();
		});

	})();

	//login && login_attempt && sign_up
	(() => {
		const loginStateKey = 'awGa4loggedState';
		const loginMethodKey = 'awGa4loginMethod';

		const subscriptionPageLoginButton = document.querySelector('.blaize-login-password');

		const magicLinkButton = document.querySelector('#aw_magic_link_button');
		const loginButton = document.querySelector('#aw_login_button');

		const userData = getUserData();

		const getSavedSidParam = () => {

			const sidParamKey = 'awGa4SidParam';

			// Saved during sign_up_submit
			const localStorageSidParam = window.localStorage.getItem(sidParamKey) || undefined;

			if (sidParam) {
				return sidParam;
			}

			if (localStorageSidParam) {
				// deleting to prevent interfering
				window.localStorage.removeItem(sidParamKey);
				return localStorageSidParam;
			}

			return undefined;
		};

		//login_attempt
		const loginAttempt = method => {
			window.dataLayer.push({
				'event': 'login_attempt',
				'event_data': {
					'method': method
				}
			});
		};

		document.addEventListener('click', event => {
			if (
				event.target === subscriptionPageLoginButton ||
				event.target === loginButton
			) {
				loginAttempt('password');
				window.localStorage.setItem(loginMethodKey, 'password');
			}

			if (event.target === magicLinkButton) {
				loginAttempt('magic link');
				window.localStorage.setItem(loginMethodKey, 'magic link');
			}

			const socialButton = event.target.closest('.zephr-form-social-sign-in-button');
			if (socialButton) {
				const buttonText = socialButton.innerText?.trim().toLowerCase() || '';
				let method = 'social';
				if (buttonText.includes('google')) {
					method = 'google';
				} else if (buttonText.includes('apple')) {
					method = 'apple';
				}
				loginAttempt(method);
				window.localStorage.setItem(loginMethodKey, method);
			}
		});

		//login & sign_up
		if (isUserLoggedIn && !window.localStorage.getItem(loginStateKey)) {

			const method = window.localStorage.getItem(loginMethodKey) || undefined;

			window.dataLayer.push({
				'event': 'login',
				'event_data': {
					'method': method,
				}
			});

			if ('sign_up' === method) {

				window.dataLayer.push({
					'event': 'sign_up',
					'user_data': {
						'user_id': userData.user_id,
						'user_org_id': userData.user_org_id,
						'user_membership_type': userData.membershipType(),
						'user_industry': userData.job_industry,
						'user_job_role': userData.job_role,
						'user_job_level': userData.job_level,
					},
					'event_data': {
						'promotion_id': getSavedSidParam(),
						// 'promotion_name': '<promotion_name>',
						'sign_up': 1
					}
				});
			}
		}

		if (isUserLoggedIn) {
			window.localStorage.setItem(loginStateKey, 'logged');
			window.localStorage.removeItem(loginMethodKey);
		} else {
			window.localStorage.removeItem(loginStateKey);
		}
	})();

	// sign_up_submit
	(() => {
		const registerButton = document.querySelector('.blaize-register-password');

		const loginMethodKey = 'awGa4loginMethod';
		const sidParamKey = 'awGa4SidParam';

		document.addEventListener('click', event => {
			if (event.target !== registerButton) {
				return;
			}

			// Saving sid param to use when firing sign_up event
			if (sidParam) {
				window.localStorage.setItem(sidParamKey, sidParam);
			}

			window.localStorage.setItem(loginMethodKey, 'sign_up');

			window.dataLayer.push({
				'event': 'sign_up_submit'
			});

		});

	})();

	// select_promotion
	(() => {
		const CTAs = [
			{
				slot: 'Footer Nav',
				location: 'Footer',
				selector: '.footer-nav a[href*="subscribe-footer"]', // Footer links
				subscribe: 1,
			},
			{
				slot: 'Header',
				location: 'Header',
				selector: '.user-nav a[href*="/subscription"]', // user nav links
				subscribe: 1,
			},
			{
				slot: 'Content overlay',
				location: 'Content',
				selector: '.payment-option-wrapper button', // Subscribe/Register wall
				[ isUserLoggedIn ? 'subscribe' : 'register' ]: 1,
			},
		];

		const clickHandler = (cta, event) => {

			const href = new URL(event.target.getAttribute('href'), URLObject.origin);
			const sid = href.searchParams.get('sid') || sidParam;

			if (!sid) {
				return;
			}

			window.dataLayer.push({ ecommerce: null });
			window.dataLayer.push({
				'event': 'select_promotion',
				'ecommerce': {
					'promotion_id': sid,
					'creative_slot': cta.slot,
					'location_id': cta.location,
					'register_cta_click': cta.register,
					'subscribe_cta_click': cta.subscribe,
				}
			});
		};

		document.addEventListener('click', event => {
			CTAs.forEach(cta => {

				const elements = document.querySelectorAll(cta.selector);

				elements.forEach(element => {

					if (element.isSameNode(event.target)) {
						clickHandler(cta, event);
					}
				});
			});
		});
	})();
};

// Getting the data that becomes available later in the loading process
(() => {

	const loadedData = {
		backendUserData: undefined,
		lyticsUserData: undefined,
		zephrExperiments: undefined,
	};

	const checkData = () => {

		const dataObjectLength = Object.keys(loadedData).length;
		const dataObjectValuesPresent = Object.values(loadedData).filter(item => item !== undefined).length;

		if (dataObjectLength === dataObjectValuesPresent) {
			awGa4DL(loadedData);
		}
	};

	// Pull user data from the backend if a user is logged in and prevent ajax call otherwise
	if (('function' === typeof window.awGetUserData) && (0 !== Object.keys(window.awGetUserData()).length)) {
		const userEmailAddress = awGetUserData().e_mail;
		const url = `${window.location.origin}/wp-json/aw-ga4/v1/user/${userEmailAddress}`;

		getBackendData(url)
			.then(result => {
				loadedData.backendUserData = result;
				checkData();
			})
			.catch(() => {
				loadedData.backendUserData = {};
				checkData();
				console.error('Can\'t get user data');
			});
	} else {
		loadedData.backendUserData = {};
		checkData();
	}

	// Get Lytics data
	new Promise((resolve, reject) => {

		const getData = (recursiveIndex = 0) => {
			const recursiveLimit = 10;

			const getAudienceId = () => {
				const dl = window.dataLayer.filter(item => item && item.lytics_segments);
				if (dl.length > 0) {
					const audienceID = Object.values(dl[0].lytics_segments).join(',');
					return audienceID;
				} else {
					return undefined;
				}
			};

			const data = {
				userId: window.Cookies.get('seerid'),
				audienceID: getAudienceId(),
			};


			if (!data.userId || !data.audienceID) {

				if (recursiveIndex >= recursiveLimit) {
					loadedData.lyticsUserData = false;
					reject();
					return;
				} else {
					setTimeout(() => {
						getData(++recursiveIndex);
					}, 500);
					return;
				}
			}

			loadedData.lyticsUserData = data;
			resolve();
		};

		getData();
	})
		.then(() => {
			checkData();
		})
		.catch((e) => {
			loadedData.lyticsUserData = {};
			checkData();
			console.error('Failed to get Lytics user data', e);
		});

	// Get Zephr experiment data
	new Promise((resolve, reject) => {

		const getZephrTestData = rawData => {
			rawData = rawData || {};

			// + using only defined items
			const data = Object.entries(rawData).filter(item => !!item[1]);

			return data.map(item => `${item[0]}:${item[1]}`).join(',') || false;
		};

		const getData = (recursiveIndex = 0) => {
			const recursiveLimit = 10;

			const zephrData = window.dataLayer.filter(item => 'zephr-pageview' === item.event)[0];

			if (!zephrData) {

				if (recursiveIndex >= recursiveLimit) {

					loadedData.zephrExperiments = false;
					reject();
					return;
				} else {

					setTimeout(() => {
						getData(++recursiveIndex);
					}, 500);
					return;
				}

			}

			loadedData.zephrExperiments = getZephrTestData(zephrData?.zephrTestGroups);
			resolve();
		};

		getData();
	})
		.then(() => {
			checkData();
		})
		.catch(() => {
			loadedData.zephrExperiments = false;
			checkData();
			console.error('Failed to get Zephr test Data');
		});
})();

// Nav attributes Lineup widgets
(() => {

	// All these arguments were added due to some lineups not having the same markup
	const addAttributes = (
		container,
		titleSelector = '.section__label',
		sectionSelector = '.section--teaser',
		sectionTitleSelector = '.section__title a',
		sectionLinkSelector = '.section__title a',
		imageLinkSelector = '.section--teaser_partner a',
		byLineSelector = '.section__byline a'
	) => {

		if (!container) {
			return;
		}

		const widgetTitleEl = container.querySelector(titleSelector);

		if (!widgetTitleEl) {
			return;
		}

		const widgetTitle = widgetTitleEl.innerText;

		const sections = container.querySelectorAll(sectionSelector);

		sections.forEach(section => {

			const sectionTitleEl = section.querySelector(sectionTitleSelector);
			const sectionTitleLinkEl = section.querySelector(sectionLinkSelector);

			if (!sectionTitleLinkEl || !sectionTitleEl) {
				return;
			}

			const sectionTitle = sectionTitleEl.innerText;

			const imgLink = section.querySelector(imageLinkSelector);

			imgLink.dataset.nav_type = 'Content-Nav';
			imgLink.dataset.nav_group = widgetTitle;
			imgLink.dataset.nav_item = sectionTitle;

			sectionTitleLinkEl.dataset.nav_type = 'Content-Nav';
			sectionTitleLinkEl.dataset.nav_group = widgetTitle;
			sectionTitleLinkEl.dataset.nav_item = sectionTitle;

			const sectionByline = section.querySelector(byLineSelector);

			if (sectionByline) {
				sectionByline.dataset.nav_type = 'Content-Nav';
				sectionByline.dataset.nav_group = widgetTitle;
				sectionByline.dataset.nav_item = sectionByline.innerText;
			}

		});
	};

	// All the lineup widgets
	window.addEventListener('sidebarLoaded', () => {

		document.querySelectorAll('.pre-footer-widget').forEach(widget => {
			addAttributes(widget);
		});
	})

	// Related posts
	window.addEventListener('relatedPostsLoaded', () => {
		addAttributes(document.querySelector('.related-posts-widget'));
	})

	// Recommended for you
	window.addEventListener('personalizedContentParselyLoaded', () => {
		addAttributes(
			document.querySelector('.aw-parsely-personalized-content'),
			'.section__label',
			'.section--teaser',
			'.section__title',
			'.section--teaser_info > a.link-reset',
			'.section--teaser_partner a',
			'.section__byline a'
		);
	})
})();
